// Professor Service - API calls for professor panel
import { useAuthStore } from '@/features/auth/stores/authStore';
import {
  ProfessorDashboardStats,
  ProfessorClassInfo,
  ClassDetailInfo,
  ClassStudentInfo,
  GradeConfigResponse,
  GradeTemplateResponse,
  CreateGradeConfigDto,
  StudentGradeResponse,
  SaveGradesDto,
  SaveGradesResponse,
  FinalizeGradesResponse,
  AttendanceSessionResponse,
  AttendanceStatsResponse,
  MarkAttendanceDto,
  MarkAttendanceResponse,
  UpdateAttendanceDto,
  UpdateAttendanceResponse,
  GetMyClassesQueryDto,
  GetAttendanceQueryDto,
  ScheduledSessionsResponse,
  SyllabusResponse,
  UploadSyllabusDto,
  UpdateSyllabusDto,
  ProfessorExamInfo,
} from '@/types/professor.types';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`;

// Reads accept the academic year being consulted; omitted means the live one.
const yearQuery = (academicYearId?: string): string =>
  academicYearId ? `?academicYearId=${academicYearId}` : '';

const getHeaders = () => {
  const tokens = useAuthStore.getState().tokens;
  const accessToken = tokens?.accessToken;
  return {
    'Content-Type': 'application/json',
    Authorization: accessToken ? `Bearer ${accessToken}` : '',
  };
};

// ==================== DASHBOARD ====================

export const fetchProfessorDashboard = async (): Promise<ProfessorDashboardStats> => {
  const response = await fetch(`${API_BASE}/professor/dashboard`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch dashboard');
  }
  return response.json();
};

// ==================== MY CLASSES ====================

export const fetchMyClasses = async (
  query: GetMyClassesQueryDto = {}
): Promise<ProfessorClassInfo[]> => {
  const params = new URLSearchParams();
  if (query.academicYearId) params.append('academicYearId', query.academicYearId);
  if (query.semester !== undefined) params.append('semester', String(query.semester));

  const url = `${API_BASE}/professor/my-classes${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch classes');
  }
  return response.json();
};

export const fetchClassDetail = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<ClassDetailInfo> => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);

  const url = `${API_BASE}/professor/my-classes/${groupeId}/module/${moduleElementId}${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch class detail');
  }
  return response.json();
};

export const fetchClassStudents = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<ClassStudentInfo[]> => {
  const response = await fetch(
    `${API_BASE}/professor/my-classes/${groupeId}/students?moduleElementId=${moduleElementId}${academicYearId ? `&academicYearId=${academicYearId}` : ''}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch class students');
  }
  return response.json();
};

// ==================== GRADE CONFIGURATION ====================

export const fetchGradeConfig = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<GradeConfigResponse | null> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/grade-config${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch grade config');
  }
  return response.json();
};

export const saveGradeConfig = async (
  groupeId: string,
  moduleElementId: string,
  dto: CreateGradeConfigDto
): Promise<GradeConfigResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/grade-config`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to save grade config');
  }
  return response.json();
};

export const deleteGradeComponent = async (configId: string): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE}/professor/grade-config/${configId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete grade component');
  }
  return response.json();
};

export const fetchGradeTemplates = async (): Promise<GradeTemplateResponse[]> => {
  const response = await fetch(`${API_BASE}/professor/grade-templates`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch grade templates');
  }
  return response.json();
};

export const applyGradeTemplate = async (
  groupeId: string,
  moduleElementId: string,
  templateId: string
): Promise<GradeConfigResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/apply-template/${templateId}`,
    {
      method: 'POST',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to apply template');
  }
  return response.json();
};

// ==================== GRADE ENTRY ====================

export const fetchStudentGrades = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<StudentGradeResponse[]> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/grades${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch student grades');
  }
  return response.json();
};

export const saveGrades = async (
  groupeId: string,
  moduleElementId: string,
  dto: SaveGradesDto
): Promise<SaveGradesResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/grades`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to save grades');
  }
  return response.json();
};

export const finalizeGrades = async (
  groupeId: string,
  moduleElementId: string
): Promise<FinalizeGradesResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/grades/finalize`,
    {
      method: 'POST',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to finalize grades');
  }
  return response.json();
};

export const unfinalizeGrades = async (
  groupeId: string,
  moduleElementId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/grades/finalize`,
    {
      method: 'DELETE',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to unfinalize grades');
  }
  return response.json();
};

// ==================== ATTENDANCE ====================

export const fetchScheduledSessions = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<ScheduledSessionsResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/scheduled-sessions${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch scheduled sessions');
  }
  return response.json();
};

export const fetchAttendanceHistory = async (
  groupeId: string,
  moduleElementId: string,
  query: GetAttendanceQueryDto = {}
): Promise<AttendanceSessionResponse[]> => {
  const params = new URLSearchParams();
  if (query.startDate) params.append('startDate', query.startDate);
  if (query.endDate) params.append('endDate', query.endDate);
  if (query.studentId) params.append('studentId', query.studentId);

  const url = `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/attendance${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch attendance history');
  }
  return response.json();
};

export const fetchAttendanceForSession = async (
  groupeId: string,
  moduleElementId: string,
  scheduleSessionId: string
): Promise<AttendanceSessionResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/attendance/session/${scheduleSessionId}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch attendance for session');
  }
  return response.json();
};

export const markAttendance = async (
  groupeId: string,
  moduleElementId: string,
  dto: MarkAttendanceDto
): Promise<MarkAttendanceResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/attendance`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to mark attendance');
  }
  return response.json();
};

export const updateAttendance = async (
  attendanceId: string,
  dto: UpdateAttendanceDto
): Promise<UpdateAttendanceResponse> => {
  const response = await fetch(`${API_BASE}/professor/attendance/${attendanceId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(dto),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update attendance');
  }
  return response.json();
};

export const fetchAttendanceStats = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<AttendanceStatsResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/attendance/stats${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch attendance stats');
  }
  return response.json();
};

export const exportAttendanceExcel = async (
  groupeId: string,
  moduleElementId: string
): Promise<Blob> => {
  const tokens = useAuthStore.getState().tokens;
  const accessToken = tokens?.accessToken;

  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/attendance/export/excel`,
    {
      method: 'GET',
      headers: {
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to export attendance to Excel');
  }
  return response.blob();
};

export const exportAttendancePdf = async (
  groupeId: string,
  moduleElementId: string
): Promise<Blob> => {
  const tokens = useAuthStore.getState().tokens;
  const accessToken = tokens?.accessToken;

  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/attendance/export/pdf`,
    {
      method: 'GET',
      headers: {
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to export attendance to PDF');
  }
  return response.blob();
};

// ==================== SYLLABUS (one per group) ====================

const classPath = (groupeId: string, moduleElementId: string): string =>
  `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}`;

export const fetchSyllabus = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<SyllabusResponse | null> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/syllabus${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch syllabus');
  }
  return response.json();
};

export const uploadSyllabus = async (
  groupeId: string,
  moduleElementId: string,
  dto: UploadSyllabusDto
): Promise<SyllabusResponse> => {
  const response = await fetch(`${classPath(groupeId, moduleElementId)}/syllabus`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(dto),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to upload syllabus');
  }
  return response.json();
};

export const updateSyllabus = async (
  groupeId: string,
  moduleElementId: string,
  dto: UpdateSyllabusDto
): Promise<SyllabusResponse> => {
  const response = await fetch(`${classPath(groupeId, moduleElementId)}/syllabus`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(dto),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update syllabus');
  }
  return response.json();
};

export const deleteSyllabus = async (
  groupeId: string,
  moduleElementId: string
): Promise<{ success: boolean }> => {
  const response = await fetch(`${classPath(groupeId, moduleElementId)}/syllabus`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (response.status === 404) {
    // Already gone (double click, other tab): the goal is reached.
    return { success: true };
  }
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete syllabus');
  }
  return response.json();
};

// ==================== COURSE CONTENT ====================

import type {
  CourseContentResponse,
  ChapterResponse,
  MaterialResponse,
  CreateChapterDto,
  UpdateChapterDto,
  CreateMaterialDto,
  UpdateMaterialDto,
} from '@/types/professor.types';

export const fetchCourseContent = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<CourseContentResponse> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/content${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch course content');
  }
  return response.json();
};

// Chapter operations
export const createChapter = async (
  groupeId: string,
  moduleElementId: string,
  dto: CreateChapterDto
): Promise<ChapterResponse> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/chapters`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create chapter');
  }
  return response.json();
};

export const updateChapter = async (
  chapterId: string,
  dto: UpdateChapterDto
): Promise<ChapterResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/chapters/${chapterId}`,
    {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update chapter');
  }
  return response.json();
};

export const deleteChapter = async (
  chapterId: string
): Promise<{ success: boolean }> => {
  const response = await fetch(
    `${API_BASE}/professor/chapters/${chapterId}`,
    {
      method: 'DELETE',
      headers: getHeaders(),
    }
  );
  if (response.status === 404) {
    // Already gone (double click, other tab): the goal is reached.
    return { success: true };
  }
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete chapter');
  }
  return response.json();
};

export const reorderChapters = async (
  groupeId: string,
  moduleElementId: string,
  chapterIds: string[]
): Promise<{ success: boolean }> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/chapters/reorder`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ chapterIds }),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to reorder chapters');
  }
  return response.json();
};

// Content settings
export const updateContentSettings = async (
  groupeId: string,
  moduleElementId: string,
  generalSectionTitle: string
): Promise<{ generalSectionTitle: string }> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/content-settings`,
    {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ generalSectionTitle }),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update content settings');
  }
  return response.json();
};

// Material operations
export const createMaterial = async (
  groupeId: string,
  moduleElementId: string,
  dto: CreateMaterialDto
): Promise<MaterialResponse> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/materials`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create material');
  }
  return response.json();
};

export const updateMaterial = async (
  materialId: string,
  dto: UpdateMaterialDto
): Promise<MaterialResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/materials/${materialId}`,
    {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update material');
  }
  return response.json();
};

export const deleteMaterial = async (
  materialId: string
): Promise<{ success: boolean }> => {
  const response = await fetch(
    `${API_BASE}/professor/materials/${materialId}`,
    {
      method: 'DELETE',
      headers: getHeaders(),
    }
  );
  if (response.status === 404) {
    // Already gone (double click, other tab): the goal is reached.
    return { success: true };
  }
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete material');
  }
  return response.json();
};

export const reorderMaterials = async (
  materialIds: string[]
): Promise<{ success: boolean }> => {
  const response = await fetch(
    `${API_BASE}/professor/materials/reorder`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ materialIds }),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to reorder materials');
  }
  return response.json();
};

export const moveMaterial = async (
  materialId: string,
  targetChapterId: string | null
): Promise<MaterialResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/materials/${materialId}/move`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ targetChapterId }),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to move material');
  }
  return response.json();
};

// ==================== ANNOUNCEMENTS ====================

import type {
  AnnouncementResponse,
  CreateAnnouncementDto,
  UpdateAnnouncementDto,
} from '@/types/professor.types';

export const fetchAnnouncements = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<AnnouncementResponse[]> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/announcements${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch announcements');
  }
  return response.json();
};

export const createAnnouncement = async (
  groupeId: string,
  moduleElementId: string,
  dto: CreateAnnouncementDto
): Promise<AnnouncementResponse> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/announcements`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create announcement');
  }
  return response.json();
};

export const updateAnnouncement = async (
  announcementId: string,
  dto: UpdateAnnouncementDto
): Promise<AnnouncementResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/announcements/${announcementId}`,
    {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update announcement');
  }
  return response.json();
};

export const deleteAnnouncement = async (
  announcementId: string
): Promise<{ success: boolean }> => {
  const response = await fetch(
    `${API_BASE}/professor/announcements/${announcementId}`,
    {
      method: 'DELETE',
      headers: getHeaders(),
    }
  );
  if (response.status === 404) {
    // Already gone (double click, other tab): the goal is reached.
    return { success: true };
  }
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete announcement');
  }
  return response.json();
};

export const toggleAnnouncementPin = async (
  announcementId: string
): Promise<AnnouncementResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/announcements/${announcementId}/toggle-pin`,
    {
      method: 'POST',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to toggle pin');
  }
  return response.json();
};

// ==================== ASSIGNMENTS ====================

import type {
  AssignmentResponse,
  AssignmentDetailResponse,
  CreateAssignmentDto,
  UpdateAssignmentDto,
  GradeSubmissionDto,
  SubmissionResponse,
} from '@/types/professor.types';

export const fetchAssignments = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<AssignmentResponse[]> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/assignments${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch assignments');
  }
  return response.json();
};

export const fetchAssignmentDetail = async (
  assignmentId: string
): Promise<AssignmentDetailResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/assignments/${assignmentId}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch assignment detail');
  }
  return response.json();
};

export const createAssignment = async (
  groupeId: string,
  moduleElementId: string,
  dto: CreateAssignmentDto
): Promise<AssignmentResponse> => {
  const response = await fetch(
    `${classPath(groupeId, moduleElementId)}/assignments`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create assignment');
  }
  return response.json();
};

export const updateAssignment = async (
  assignmentId: string,
  dto: UpdateAssignmentDto
): Promise<AssignmentResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/assignments/${assignmentId}`,
    {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update assignment');
  }
  return response.json();
};

export const deleteAssignment = async (
  assignmentId: string
): Promise<{ success: boolean }> => {
  const response = await fetch(
    `${API_BASE}/professor/assignments/${assignmentId}`,
    {
      method: 'DELETE',
      headers: getHeaders(),
    }
  );
  if (response.status === 404) {
    // Already gone (double click, other tab): the goal is reached.
    return { success: true };
  }
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete assignment');
  }
  return response.json();
};

export const gradeSubmission = async (
  submissionId: string,
  dto: GradeSubmissionDto
): Promise<SubmissionResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/submissions/${submissionId}/grade`,
    {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to grade submission');
  }
  return response.json();
};

// ==================== RATTRAPAGE ====================

export interface StudentRattrapageItem {
  studentId: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  rattrapageId: string;
  reason: string;
  originalGrade: number | null;
  rattrapageGrade: number | null;
  cappedGrade: number | null;
  absenceCount?: number;
  status: 'PENDING' | 'GRADED';
  isFinalized?: boolean;
  gradedAt?: Date;
  gradedBy?: string;
  comments?: string;
}

export interface StudentRattrapageResponse {
  students: StudentRattrapageItem[];
  rattrapageGradeCap: number | null;
  // Once finalized the professor can no longer edit; canFinalize is false
  // while a rattrapage is still ungraded.
  isFinalized: boolean;
  canFinalize: boolean;
}

export const fetchStudentsWithRattrapage = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<StudentRattrapageResponse> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/rattrapages${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch students with rattrapage');
  }
  return response.json();
};

export const checkHasStudentsWithRattrapage = async (
  groupeId: string,
  moduleElementId: string,
  academicYearId?: string
): Promise<{ hasRattrapages: boolean }> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/has-rattrapages${yearQuery(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to check rattrapage status');
  }
  return response.json();
};

export const finalizeRattrapageGrades = async (
  groupeId: string,
  moduleElementId: string
): Promise<{ success: boolean; message: string; finalizedCount: number }> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/rattrapages/finalize`,
    {
      method: 'POST',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to finalize rattrapage grades');
  }
  return response.json();
};

export const saveRattrapageGrades = async (
  groupeId: string,
  moduleElementId: string,
  dto: { grades: { studentId: string; rattrapageGrade: number; comments?: string }[] }
): Promise<{ savedCount: number }> => {
  const response = await fetch(
    `${API_BASE}/professor/classes/${groupeId}/module/${moduleElementId}/rattrapages`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to save rattrapage grades');
  }
  return response.json();
};

// ==================== FILE UPLOAD ====================

export interface UploadedFileResponse {
  success: boolean;
  file: {
    name: string;
    fileName: string;
    url: string;
    type: string;
    fileType: string;
    size: number;
    fileSize: number;
  };
}

/**
 * Upload a file to the server for assignments
 * Returns a persistent server URL that can be accessed by students
 */
export const uploadAssignmentFile = async (file: File): Promise<UploadedFileResponse> => {
  const tokens = useAuthStore.getState().tokens;
  const accessToken = tokens?.accessToken;

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE}/professor/upload-file`, {
    method: 'POST',
    headers: {
      Authorization: accessToken ? `Bearer ${accessToken}` : '',
      // Don't set Content-Type - let the browser set it with the boundary
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to upload file');
  }

  return response.json();
};

// Exams and rattrapages the professor is concerned by: published entries on
// the module/group pairs they teach this year, or where they proctor. The
// server decides both; the client never filters by professor.
export const fetchMyExams = async (academicYearId?: string): Promise<ProfessorExamInfo[]> => {
  const url = `${API_BASE}/professor/exams${yearQuery(academicYearId)}`;
  const response = await fetch(url, { method: 'GET', headers: getHeaders() });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch exams');
  }
  return response.json();
};
